import { fromEventPattern, pipe } from 'rxjs'
import { distinctUntilChanged, filter, flatMap, map, tap } from 'rxjs/operators'
import { IVolumeControls } from '../models'
import Hls, { Level, FragParsedData } from 'hls.js'
import { VideoAdsService } from '../services/video-ads.service'
import { IVideoLoggingPostBody } from '../services/video-event-logging.service'
import { IVideoAdsLibrary } from '../interfaces/video-ads-library.interface'
import { SegmentEvents } from 'src/app/shared/analytics/models/segment-events.model'
import { groupBy } from 'fp-ts/lib/NonEmptyArray'
import { ConsolidatedLiveEvent, LiveHub } from 'src/app/events/interfaces/event.interface'
import { IExtendedRealtimeCameraInfo, IRealtimeEventResponseWithEventId } from '../models/realtime-event-response.model'
import { DatePipe } from '@angular/common'
import { addDays, endOfToday, isAfter, isBefore, isToday, startOfToday } from 'date-fns'
import { legacyParse } from '@date-fns/upgrade/v2'

/**
 * function to deep dive into the fragParsingData sent back from hls.js
 * to ensure that the fragData includes a cue out tag. If true then we can send the whole
 * fragParsingData object downstream for logging purposes
 */
export const fragIncludesCueOutTag = (fragData: FragParsedData) => {
  return !!fragData.frag?.tagList?.filter(
    (tags: ReadonlyArray<string>) => tags.includes('EXT-X-CUE-OUT') || tags.includes('EXT-X-DATERANGE')
  ).length
}

export const fromAdsEvent = (
  ads: google.ima.AdsLoader | google.ima.AdsManager,
  eventName: google.ima.AdEvent.Type | google.ima.AdErrorEvent.Type | google.ima.AdsManagerLoadedEvent.Type
) =>
  fromEventPattern(
    (callback: any) => ads.addEventListener(eventName as any, callback, false),
    (callback: any) => ads.removeEventListener(eventName as any, callback)
  )

export const flatMapAdClicked = () =>
  flatMap(([adsManager, videoAdsLibrary]: [google.ima.AdsManager, IVideoAdsLibrary]) =>
    fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.CLICK)
  )

export const flatMapAdImpression = () =>
  flatMap(([adsManager, videoAdsLibrary]: [google.ima.AdsManager, IVideoAdsLibrary]) =>
    fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.IMPRESSION)
  )

export const flatMapVolumeChanged = () =>
  flatMap(([adsManager, videoAdsLibrary]: [google.ima.AdsManager, IVideoAdsLibrary]) =>
    fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.VOLUME_CHANGED)
  )

export const flatMapAdsManagerLoaded = () =>
  flatMap(([adsLoader, videoAdsLibrary]: [google.ima.AdsLoader, IVideoAdsLibrary]) =>
    fromAdsEvent(adsLoader, videoAdsLibrary.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED)
  )

export const flatMapAdsLoader = (videoAdsService: VideoAdsService) =>
  flatMap((adDisplayContainer: google.ima.AdDisplayContainer) => videoAdsService.getAdsLoader$(adDisplayContainer))

export const registerContentCompleteListener = () =>
  tap(([adsLoader, contentVideoElement]: [google.ima.AdsLoader, HTMLVideoElement]) => {
    contentVideoElement.addEventListener('ended', () => adsLoader.contentComplete())
  })

export const mapGetAdsManager = () =>
  map(([adsManagerLoadedEvent, contentVideoElement]: [google.ima.AdsManagerLoadedEvent, HTMLVideoElement]) => {
    const adsRenderingSettings = new google.ima.AdsRenderingSettings()
    adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true
    return adsManagerLoadedEvent.getAdsManager(contentVideoElement, adsRenderingSettings)
  })

export const flatMapContentResumeRequested = () =>
  flatMap(([adsManager, videoAdsLibrary]: [google.ima.AdsManager, IVideoAdsLibrary]) =>
    fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.CONTENT_RESUME_REQUESTED)
  )

export const flatMapContentPauseRequested = () =>
  flatMap(([adsManager, videoAdsLibrary]: [google.ima.AdsManager, IVideoAdsLibrary]) =>
    fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.CONTENT_PAUSE_REQUESTED)
  )

export const flatMapContentPause = () =>
  flatMap(([adsManager, videoAdsLibrary]: [google.ima.AdsManager, IVideoAdsLibrary]) =>
    fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.PAUSED)
  )

export const flatMapContentResume = () =>
  flatMap(([adsManager, videoAdsLibrary]: [google.ima.AdsManager, IVideoAdsLibrary]) =>
    fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.RESUMED)
  )

export const flatMapContentFirstQuartile = () =>
  flatMap(([adsManager, videoAdsLibrary]: [google.ima.AdsManager, IVideoAdsLibrary]) =>
    fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.FIRST_QUARTILE)
  )

export const flatMapContentMidpoint = () =>
  flatMap(([adsManager, videoAdsLibrary]: [google.ima.AdsManager, IVideoAdsLibrary]) =>
    fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.MIDPOINT)
  )

export const flatMapContentThirdQuartile = () =>
  flatMap(([adsManager, videoAdsLibrary]: [google.ima.AdsManager, IVideoAdsLibrary]) =>
    fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.THIRD_QUARTILE)
  )

export const flatMapAdStarted = () =>
  flatMap(([adsManager, videoAdsLibrary]: [google.ima.AdsManager, IVideoAdsLibrary]) =>
    fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.STARTED)
  )

export const flatMapAllAdsCompleted = () =>
  flatMap(([adsManager, videoAdsLibrary]: [google.ima.AdsManager, IVideoAdsLibrary]) =>
    fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.ALL_ADS_COMPLETED)
  )

export const flatMapAdCompleted = () =>
  flatMap(([adsManager, videoAdsLibrary]: [google.ima.AdsManager, IVideoAdsLibrary]) =>
    fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.COMPLETE)
  )

export const flatMapAdErrors = () =>
  flatMap(([ads, videoAdsLibrary]: [google.ima.AdsManager | google.ima.AdsLoader, IVideoAdsLibrary]) =>
    fromAdsEvent(ads, videoAdsLibrary.AdErrorEvent.Type.AD_ERROR)
  )

export const flatMapAdDisplayContainer = (videoAdsService: VideoAdsService) =>
  flatMap(([adContainerElement, contentVideoElement]: [HTMLElement, HTMLVideoElement]) =>
    videoAdsService.getAdDisplayContainer$(contentVideoElement, adContainerElement)
  )

export const adCueOutDurationRegEx = /((?<=DURATION\s*=\s*)(\d+\.*\d*))|^(\d+\.*\d+)/
export const getAdDurationFromCueOutTag = (adCueOutUrl: string) => {
  const search = adCueOutDurationRegEx.exec(adCueOutUrl)
  return Math.ceil(Number((search && search[0]) || '0'))
}

export const checkAdMarkersFromCueOutTag = (tags: Array<string>) => {
  if (tags.includes('EXT-X-CUE-OUT')) {
    return true
  } else {
    if (tags.includes('EXT-X-DATERANGE') && tags[1].indexOf('SCTE35-OUT') !== -1) {
      return true
    }
  }
  return false
}

export const getEventStatusFromAssetTag = (eventStatus: string) => {
  const status = eventStatus.match(/=([^,]*)/)
  return status ? status[1] : ''
}

export const mapAdEventsToSegment = (videoAdsLibrary: IVideoAdsLibrary) => {
  return {
    [videoAdsLibrary.AdEvent.Type.STARTED]: SegmentEvents.AD_STARTED,
    [videoAdsLibrary.AdEvent.Type.COMPLETE]: SegmentEvents.AD_COMPLETED,
    [videoAdsLibrary.AdEvent.Type.CLICK]: SegmentEvents.AD_CLICKED,
    [videoAdsLibrary.AdEvent.Type.PAUSED]: SegmentEvents.AD_PAUSED,
    [videoAdsLibrary.AdEvent.Type.RESUMED]: SegmentEvents.AD_RESUMED,
    [videoAdsLibrary.AdEvent.Type.FIRST_QUARTILE]: SegmentEvents.AD_FIRST_QUARTILE,
    [videoAdsLibrary.AdEvent.Type.MIDPOINT]: SegmentEvents.AD_MIDPOINT,
    [videoAdsLibrary.AdEvent.Type.THIRD_QUARTILE]: SegmentEvents.AD_THIRD_QUARTILE,
    [videoAdsLibrary.AdEvent.Type.VOLUME_CHANGED]: SegmentEvents.VOLUME_CHANGED,
    [videoAdsLibrary.AdErrorEvent.Type.AD_ERROR]: SegmentEvents.AD_ERRORED
  }
}

export const distinctUntilAdTagUrlChanged = () =>
  distinctUntilChanged(
    (oldValue: google.ima.AdsRequest, newValue: google.ima.AdsRequest) => oldValue.adTagUrl === newValue.adTagUrl
  )

export const mapToVideoAdLog = () =>
  map(([_videoAdLogPartial, adUrl]) => {
    const videoAdLogPartial = _videoAdLogPartial || {}
    return {
      ...videoAdLogPartial,
      context: {
        ...videoAdLogPartial.context,
        value: {
          adUrl
        }
      }
    }
  })

export const flatten = <Type>(array: ReadonlyArray<Type>): ReadonlyArray<Type> =>
  array.reduce(
    (accumulator: ReadonlyArray<Type>, element: Type) =>
      accumulator.concat(Array.isArray(element) ? flatten(element) : element),
    []
  )

export const getDescendantElements = (element: HTMLElement): ReadonlyArray<any> => [
  ...Array.from(element.children),
  ...(Array.from(element.children) || []).map((child: HTMLElement) => getDescendantElements(child))
]

export const setAdVolume = ([[isAdPlaying, volumeControls], contentVideoElement, adsManager]: [
  [boolean, IVolumeControls],
  HTMLVideoElement,
  google.ima.AdsManager
]) => {
  if (volumeControls.isSelectedStream) {
    const currentVolume = volumeControls.isMuted ? 0 : volumeControls.volume
    // stream is selected and ad is playing - mute content and apply volume to ad
    contentVideoElement.muted = true
    adsManager.setVolume(currentVolume)
  } else {
    // stream is not selected - mute ad and mute content
    contentVideoElement.muted = true
    adsManager.setVolume(0)
  }
}

export const formatCustomParams = (params: Record<string, unknown>) => {
  return Object.entries(params)
    .reduce((acc, curr) => {
      return [...acc, `${curr[0]}=${curr[1]}`]
    }, [])
    .filter(param => param && param.length)
    .join('&')
}

interface GoogleIMA {
  google: {
    ima: IVideoAdsLibrary
  }
}

export const mapVideoAdsLibrary = (window: Window) =>
  map(() => {
    const googleIMA = (window as Window & GoogleIMA).google
    return googleIMA ? googleIMA.ima : undefined
  })

export const mapInitializedAdContainer = (contentVideoElement: HTMLVideoElement, adContainerElement: HTMLElement) =>
  map((videoAdsLibrary: IVideoAdsLibrary) => {
    videoAdsLibrary.settings.setVpaidMode(videoAdsLibrary.ImaSdkSettings.VpaidMode.INSECURE)
    const adDisplayContainer = new (videoAdsLibrary as any).AdDisplayContainer(
      adContainerElement,
      contentVideoElement
    ) as google.ima.AdDisplayContainer
    adDisplayContainer.initialize()
    return adDisplayContainer
  })

export const mapNewAdsLoader = (adDisplayContainer: google.ima.AdDisplayContainer) =>
  map(videoAdsLibrary => new (videoAdsLibrary as any).AdsLoader(adDisplayContainer) as google.ima.AdsLoader)

const getHead = (array: ReadonlyArray<any>) => array[0]

export const mapGetHead = () => map(getHead)

export const getAdEventLog = (log: IVideoLoggingPostBody = {} as IVideoLoggingPostBody, message: string) => ({
  ...log,
  context: {
    ...log.context,
    message
  }
})

export const fromHlsEvent = <T>(hls: Hls, eventName: string | any) =>
  fromEventPattern<T>(
    (callback: any) => hls.on(eventName, callback),
    (callback: any) => hls.off(eventName, callback)
  )

export const flatMapFragParsingData = <T>() => flatMap((hls: Hls) => fromHlsEvent<T>(hls, Hls.Events.FRAG_PARSED))

type MapFunction = <T>(value: any, index: number) => T
export const safeMap = <T>(mapFunction: MapFunction) => pipe(map(mapFunction), filter<T>(Boolean))
export const mapChanges = <T>(key: string) =>
  pipe(
    safeMap(simpleChanges => simpleChanges[key]),
    safeMap<T>(simpleChange => simpleChange.currentValue)
  )

/**
 * Takes in an object if the object has an array length more than 2, the function will return a shallow copy of the array that starts at 2
 */
export const splitRightRailContent = (content: any) => (content.shouldShow > 2 ? content.items.slice(2) : null)

/**
 * Determine whether to show video controls on live player to user
 *
 * isEventLive: boolean, eventStatus === 'LIVE'
 * adPlaying: boolean
 * dvrPreAir: boolean, eventStatus === 'PRE-AIR'
 * isDVR: boolean, event.isDVR
 */
export const canShowLiveVideoControls = (
  isEventLive: boolean,
  adPlaying: boolean,
  dvrPreAir: boolean,
  isDVR: boolean
): boolean => {
  return (isEventLive && !adPlaying) || (!dvrPreAir && isDVR && !adPlaying)
}

/**
 * Video Ad Error Codes
 *
 * 300: google.ima.AdError.ErrorCode.VAST_WRAPPER_ERROR
 * 301: google.ima.AdError.ErrorCode.VAST_LOAD_TIMEOUT
 * 302: google.ima.AdError.ErrorCode.VAST_TOO_MANY_REDIRECTS
 * 303: google.ima.AdError.ErrorCode.VAST_NO_ADS_AFTER_WRAPPER
 * 402: google.ima.AdError.ErrorCode.VAST_MEDIA_LOAD_TIMEOUT
 * 408: Rejected ad in VAST Response
 * 600: General CompanionAds error
 * 602: google.ima.AdError.ErrorCode.COMPANION_REQUIRED_ERROR
 * 603: google.ima.AdError.ErrorCode.COMPANION_AD_LOADING_FAILED
 * 900: google.ima.AdError.ErrorCode.UNKNOWN_ERROR
 * 1005: google.ima.AdError.ErrorCode.FAILED_TO_REQUEST_ADS
 * 1009: google.ima.AdError.ErrorCode.VAST_EMPTY_RESPONSE
 *
 * Class AdError:
 * @see {@link https://developers.google.com/interactive-media-ads/docs/sdks/html5/client-side/reference/js/google.ima.AdError#.ErrorCode}
 *
 * Error Codes:
 * @see {@link https://support.google.com/admanager/answer/4442429}
 */
const IGNORED_VAST_ERRORS = [300, 301, 302, 303, 402, 408, 600, 602, 603, 900, 1005, 1009]

/**
 * Certain 901 errors we want to ignore depending on the inner error
 */
const VPAID_ERROR_CODE = 901
/**
 * Inner errors to ignore within the 901 error
 */
const IGNORED_901_INNER_ERRORS = ['NO_FILL', 'SUPPLY_TAG_TIMEOUT']

/**
 * returns the AdError type if the param passed in is an AdErrorEvent type else stringify other errors
 * @param adErrorEvent
 * @returns google.ima.AdError | string
 */
export const getAdError = (adErrorEvent: google.ima.AdErrorEvent | any): google.ima.AdError => {
  return adErrorEvent?.getError?.() || JSON.stringify(adErrorEvent)
}

/**
 * takes either adErrorEvent or other errors and checks if the error should be ignored or not
 * @param adErrorEvent
 * @returns boolean
 */
export const isFatalAdError = (adErrorEvent: google.ima.AdErrorEvent | any): boolean => {
  const adError = getAdError(adErrorEvent)
  const errorCode = adError?.getErrorCode?.() || 0
  const innerAdError = adError?.getInnerError?.() || ''
  const hasInnerError = !!IGNORED_901_INNER_ERRORS.find(
    ignoredErr => innerAdError.toString().indexOf(ignoredErr) !== -1
  )

  let isFatalError = !IGNORED_VAST_ERRORS.includes(errorCode)
  // Handle VPAID Errors - 901 and includes the inner error is 'NO_FILL'
  if (errorCode === VPAID_ERROR_CODE && hasInnerError) {
    isFatalError = false
  }

  return isFatalError
}

export const getHlsQualityLevels = (levels: Level[]) => {
  const qualityLevels = Object.values(
    // Group by height e.g. 720, 1080
    groupBy((level: Level): string => String(level.height))(levels)
  )
    .flatMap(groupedLevels => {
      return (
        Object.values(groupedLevels)
          // Sort by height followed by bitrate to ensure proper ordering
          .sort((a, b) => a.height - b.height || a.bitrate - b.bitrate)
          .map((level, index) => {
            return {
              // Generate label string, e.g. 1080, 1080+, 1080++, etc
              label: `${level.height}${'+'.repeat(index)}`
            }
          })
      )
    })
    .map((level, index) => ({ ...level, id: index }))

  return qualityLevels
}

export const formatDate = (dp: DatePipe) => (isTbd?: boolean) => (start?: string) => (end?: string | Date) => {
  const startTime = dp.transform(start, 'shortTime')
  const startDate = dp.transform(start, 'MMM d')
  const endDate = dp.transform(end, 'MMM d')
  const endDateText = endDate === startDate ? '' : ` - ${endDate}`

  return `${startDate}${endDateText} @ ${isTbd ? 'TBD' : startTime}`
}

export const extractRailEventsFromRouteData =
  (dp: DatePipe) =>
  (currentEventId?: number | string) =>
  (title: string) =>
  (forceEventHub = false) =>
  (events: ReadonlyArray<ConsolidatedLiveEvent>) => {
    const groupedEvents =
      title === 'Today'
        ? events.filter(
            event =>
              (event.start_date && isToday(legacyParse(event.start_date))) ||
              (event.start_date &&
                event.end_date &&
                isBefore(legacyParse(event.start_date), startOfToday()) &&
                isAfter(legacyParse(event.end_date), startOfToday()))
          )
        : events.filter(event => event.start_date && isAfter(legacyParse(event.start_date), endOfToday()))
    const statusOrder: { [liveStatus: string]: number } = {
      LIVE: 0,
      INTERMISSION: 1,
      'PRE-AIR': 2,
      CONCLUDED: 3
    }
    const sortedEvents = groupedEvents
      .sort((a, b) => new Date(a.start_date).getUTCMilliseconds() - new Date(b.start_date).getUTCMilliseconds())
      .sort((a, b) => statusOrder[a.live_status] - statusOrder[b.live_status])

    const items = sortedEvents.map(mapEventToRowItem(dp)(currentEventId)(forceEventHub))

    return {
      title,
      items,
      shouldShow: items.length
    }
  }
export const extractLiveStatusConsolidatedEvent = (evt: ConsolidatedLiveEvent) => evt.live_status === 'LIVE'

export const extractRouterLinkFromConsolidatedEvent =
  (evt: ConsolidatedLiveEvent) =>
  (forceEventHub = false) => {
    if (!forceEventHub) {
      return `../${evt.live_id}`
    } else {
      return evt.slug_uri ? `${evt.slug_uri}` : '../events'
    }
  }
export const extractEventHubLinkFromRoutedEvent = (eventSummary: LiveHub) => {
  return (eventSummary?.event?.slug_uri && `${eventSummary?.event?.slug_uri}`) || 'events'
}

export const appendImageWidthParam = (width: number) => (url: string) => `${url}?width=${width}`

export const extractThumbnailFromConsolidatedEvent = (evt: ConsolidatedLiveEvent) =>
  evt.asset_url ? appendImageWidthParam(250)(evt.asset_url) : ''

export const mapEndDate = (startDate: string) => (numberOfDays: number) => {
  return addDays(legacyParse(startDate), numberOfDays - 1)
}

export const mapEventToRowItem =
  (dp: DatePipe) =>
  (currentEventId?: number | string) =>
  (forceEventHub = false) =>
  (evt: ConsolidatedLiveEvent) => {
    return {
      liveId: evt.live_id,
      title: evt.short_title,
      isSelected: currentEventId === evt.live_id,
      isLive: extractLiveStatusConsolidatedEvent(evt),
      isReplay: evt.live_status === 'CONCLUDED' && evt.live_dvr,
      thumbnail: extractThumbnailFromConsolidatedEvent(evt),
      routerLink: extractRouterLinkFromConsolidatedEvent(evt)(forceEventHub),
      date: formatDate(dp)(evt.start_time_tbd)(evt.start_date)(mapEndDate(evt.start_date)(evt.number_of_days)),
      analyticProps: {
        node_id: evt.id,
        live_id: evt.live_id
      }
    }
  }

export const isLive = (status: string): boolean => {
  return status === 'LIVE'
}

interface IRealtimeEventResponseWithMappedStreams {
  event: IRealtimeEventResponseWithEventId
  mappedStreams: ReadonlyArray<IExtendedRealtimeCameraInfo>
}

interface RoutedEvent {
  realtime: IRealtimeEventResponseWithMappedStreams
  cms: LiveHub
}
